import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  Box,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { forgotPasswordSendOtp, forgotPasswordValidateOtp, forgotPassword } from "../../action/password";
import { setAlert, clearAlerts } from "../../action/alert";
import { validatePassword } from "../../utils/general";
import Dialog from "../Common/Dailog/Dailog";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";

const ForgotPassword = ({
  open,
  onClose,
  forgotPasswordSendOtp,
  forgotPasswordValidateOtp,
  forgotPassword,
  clearAlertsAction,
  setAlertAction,
}) => {
  const MAX_OTP_ATTEMPTS = 3;
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpAttemptCount, setOtpAttemptCount] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");
  const [counter, setCounter] = useState(30);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const otpVerifyInFlightRef = useRef(false);
  const sendOtpRequestGenRef = useRef(0);
  const verifyOtpRequestGenRef = useRef(0);

  // Global alert skips new messages while a snackbar is open; clear first so newest always shows.
  const showToast = (message, severity = "danger") => {
    clearAlertsAction();
    setAlertAction(message, severity);
  };

  useEffect(() => {
    if (step === 2) setCounter(30);
  }, [step]);

  useEffect(() => {
    if (step !== 2 || counter <= 0) return;

    const timerId = setInterval(() => {
      setCounter((c) => c - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [step, counter]);

  const handleReset = () => {
    setStep(1);
    setEmail("");
    setOtp("");
    setOtpAttemptCount(0);
    setError("");
    setNewPassword("");
    setConfirmPassword("");
    setIsSendingOtp(false);
    setIsVerifyingOtp(false);
    otpVerifyInFlightRef.current = false;
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const sendOtp = () => {
    if (isSendingOtp) return;
    setError("");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    if(!emailRegex.test(email)){
      setError("Please enter a valid email address.")
      return;
    }

    const requestGen = ++sendOtpRequestGenRef.current;
    setIsSendingOtp(true);
    forgotPasswordSendOtp(email)
      .then((res) => {
        if (requestGen !== sendOtpRequestGenRef.current) return;
        if (res?.status === 200) {
          setError("");
          setOtpAttemptCount(0);
          setStep(2);
        } else {
          showToast("Unable to send OTP. Please try again.");
        }
      })
      .catch((err) => {
        if (requestGen !== sendOtpRequestGenRef.current) return;
        const backendMessage = err?.response?.data?.message;
        const userMessage = err?.userMessage;
        const fallbackMessage = "Failed to send OTP.";
        const message = backendMessage || userMessage || fallbackMessage;
        showToast(message, "danger");
      })
      .finally(() => {
        setIsSendingOtp(false);
      });
  };

  const handleInvalidOtpAttempt = () => {
    setOtpAttemptCount((prev) => {
      const next = prev + 1;
      const remainingAttempts = MAX_OTP_ATTEMPTS - next;
      if (remainingAttempts <= 0) {
        showToast("Maximum OTP attempts exceeded. Please login again.", "danger");
        handleClose();
        return 0;
      }
      showToast(`Invalid OTP. ${remainingAttempts} attempt(s) remaining.`, "danger");
      return next;
    });
  };

  const verifyOtp = () => {
    if (isVerifyingOtp || otpVerifyInFlightRef.current) return;
    setError("");
    if (!otp) {
      setError("Please enter OTP.");
      return;
    }
    if (otp.length !== 6) {
      setError("Please enter valid otp.");
      return;
    }

    otpVerifyInFlightRef.current = true;
    const requestGen = ++verifyOtpRequestGenRef.current;
    setIsVerifyingOtp(true);
    forgotPasswordValidateOtp(email, otp)
      .then((res) => {
        if (requestGen !== verifyOtpRequestGenRef.current) return;
        if (res?.status === 200 && res?.data?.token) {
          setToken(res?.data?.token);
          setOtpAttemptCount(0);
          setError("");
          setStep(3);
        } else {
          handleInvalidOtpAttempt();
        }
      })
      .catch(() => {
        if (requestGen !== verifyOtpRequestGenRef.current) return;
        handleInvalidOtpAttempt();
      })
      .finally(() => {
        setIsVerifyingOtp(false);
        otpVerifyInFlightRef.current = false;
      });
  };

  const resetPassword = () => {
    setError("");

    if (!newPassword || !confirmPassword) {
      setError("Please fill both fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const { valid, error: validationError } = validatePassword(newPassword);
    if (!valid) {
      setError(validationError);
      return;
    }

    forgotPassword(email, newPassword, token)
      .then((res) => {
        if (res?.status === 200) {
          setError("");
          setSuccess("Your password has been reset successfully. Please sign in again.");
          showToast("Password reset successful!", "success");
          setNewPassword("");
          setConfirmPassword("");
          onClose();
        } else {
          showToast("Unable to reset password. Please try again.");
        }
      })
      .catch((err) => {
        if (err?.response?.status === 500) {
          showToast(err?.response?.data?.message || err?.userMessage || "Password reset failed.", "danger");
        } else {
          showToast(err?.userMessage || "Password reset failed.");
        }
      });
  };

  const goBackToStep1 = () => {
    setStep(1);
    setOtp("");
    setOtpAttemptCount(0);
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={step === 1 ? "Reset Password" : step === 2 ? "Enter OTP" : "Set New Password"}
      maxWidth="xs"
      fullWidth
      closeOnBackdropClick
      closeOnEscapeKeyDown
      contentSx={{ overflow: "visible", pt: 2, pb: 2 }}
    >
      <Box sx={{ width: "100%" }}>
        <Stack spacing={2}>
          {step === 1 && (
            <>
              <Typography variant="body2">
                Enter your registered email address to receive an OTP.
              </Typography>
              <FormInput
                label="Email Address"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                error={Boolean(error)}
                helperText={error || ""}
                touched="true"
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
                <AppButton variant="ghost" onClick={handleClose}>
                  Cancel
                </AppButton>
                <AppButton variant="primary" onClick={sendOtp} disabled={isSendingOtp}>
                  Continue
                </AppButton>
              </Box>
            </>
          )}

          {step === 2 && (
            <>
              <Typography variant="body2">
                We sent an OTP to <strong>{email}</strong>
              </Typography>
              <FormInput
                label="OTP"
                type="text"
                fullWidth
                value={otp}
                onChange={(e) => {
                  if (/^\d{0,6}$/.test(e.target.value)) {
                    setOtp(e.target.value);
                  }
              
                }}
                autoFocus
                error={Boolean(error)}
                helperText={error || ""}
                touched="true"
              />
              <Box>
                {counter > 0 ? (
                  <Typography variant="caption" color="textSecondary">
                    Resend OTP in {counter}s
                  </Typography>
                ) : (
                  <Typography variant="caption">
                    Didn't receive it?{" "}
                    <MuiLink
                      component="button"
                      type="button"
                      onClick={() => {
                        sendOtp();
                        setOtp("");
                        setError("");
                        setCounter(30);
                      }}
                      underline="always"
                      sx={{ color: "primary.main", cursor: "pointer", background: "transparent", border: 0, p: 0 }}
                    >
                      Resend OTP
                    </MuiLink>
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
                <AppButton variant="ghost" onClick={goBackToStep1}>
                  Back
                </AppButton>
                <AppButton variant="primary" onClick={verifyOtp} disabled={isVerifyingOtp}>
                  Continue
                </AppButton>
              </Box>
            </>
          )}

          {step === 3 && (
            <>
              <FormInput
                label="New Password"
                type="password"
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
                error={Boolean(error)}
                helperText={error || ""}
                touched="true"
              />
              <FormInput
                label="Confirm Password"
                type="password"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={Boolean(error)}
                helperText={error || ""}
                touched="true"
              />
              {success && (
                <Typography color="primary" variant="body2">
                  {success}
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
                <AppButton variant="ghost" onClick={goBackToStep1}>
                  Back
                </AppButton>
                <AppButton variant="primary" onClick={resetPassword}>
                  Submit
                </AppButton>
              </Box>
            </>
          )}
        </Stack>
      </Box>

    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => ({
  forgotPasswordSendOtp: (email) => dispatch(forgotPasswordSendOtp(email)),
  forgotPasswordValidateOtp: (email, otp) => dispatch(forgotPasswordValidateOtp(email, otp)),
  forgotPassword: (email, newPassword, token) =>
    dispatch(forgotPassword(email, newPassword, token)),
  clearAlertsAction: () => dispatch(clearAlerts()),
  setAlertAction: (message, status) => dispatch(setAlert(message, status)),
});

export default connect(null, mapDispatchToProps)(ForgotPassword);
